import {
    Cancel as CancelIcon,
    CheckCircle as CheckIcon,
    ChangeCircle as ConvertIcon,
    CalendarToday as DateIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    FilterList as FilterIcon,
    CurrencyRupee as MoneyIcon,
    MoreVert as MoreIcon,
    Pending as PendingIcon,
    Phone as PhoneIcon,
    Print as PrintIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Grid from '@mui/material/Grid';

const EnquiryAdmin = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const apiURL = process.env.REACT_APP_API;

    // Get initial values from URL
    const initialSearch = searchParams.get('search') || '';
    const initialStatus = searchParams.get('status') || 'all';
    const initialPage = parseInt(searchParams.get('page')) || 1;

    // State
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState(initialSearch);
    const [searchInput, setSearchInput] = useState(initialSearch); // For typing without triggering API
    const [statusFilter, setStatusFilter] = useState(initialStatus);
    const [page, setPage] = useState(initialPage);
    const [pagination, setPagination] = useState({
        current_page: 1,
        limit: 10,
        total_records: 0,
        total_pages: 1
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [convertDialogOpen, setConvertDialogOpen] = useState(false);
    const [convertLoading, setConvertLoading] = useState(false);
    const [enquiryToConvert, setEnquiryToConvert] = useState(null);

    // Status options
    const statusOptions = [
        { value: 'all', label: 'All Status' },
        { value: 'pending', label: 'Pending' },
        { value: 'converted', label: 'Converted' },
        { value: 'rejected', label: 'Rejected' }
    ];

    // Referral source mapping
    const referralSourceMap = {
        friend: 'Friend/Family',
        social_media: 'Social Media',
        website: 'Website',
        search_engine: 'Search Engine',
        newspaper: 'Newspaper/Ad',
        seminar: 'Seminar',
        alumni: 'Alumni',
        other: 'Other'
    };

    // Fetch enquiries from API
    const fetchEnquiries = async (searchValue = search, statusValue = statusFilter, pageValue = page) => {
        setLoading(true);
        try {
            // Build query parameters
            const params = new URLSearchParams();
            if (searchValue) params.append('search', searchValue);
            if (statusValue && statusValue !== 'all') params.append('status', statusValue);
            params.append('page', pageValue);

            const response = await axios.get(`${apiURL}/franchise/enquiry/getall?${params.toString()}`);

            if (response.data.status === 'success' || response.data.status === true) {
                setEnquiries(response.data.data || []);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            } else {
                toast.error(response.data.message || 'Failed to fetch enquiries');
                setEnquiries([]);
            }
        } catch (error) {
            console.error('Error fetching enquiries:', error);
            toast.error(error.response?.data?.message || 'Failed to fetch enquiries');
            setEnquiries([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data when search, status, or page changes
    useEffect(() => {
        fetchEnquiries();
    }, [search, statusFilter, page]);

    // Update URL when search, filter, or page changes
    const updateURL = (searchValue, statusValue, pageValue) => {
        const params = new URLSearchParams();
        if (searchValue) params.set('search', searchValue);
        if (statusValue && statusValue !== 'all') params.set('status', statusValue);
        if (pageValue > 1) params.set('page', pageValue);
        setSearchParams(params);
    };

    const handleSearch = (e) => {
        setSearchInput(e.target.value);
    };

    const handleSearchSubmit = (e) => {
        if (e.key === 'Enter') {
            setSearch(searchInput);
            setPage(1); // Reset to first page on search
            updateURL(searchInput, statusFilter, 1);
        }
    };

    const handleStatusFilter = (e) => {
        const value = e.target.value;
        setStatusFilter(value);
        setPage(1); // Reset to first page on filter change
        updateURL(search, value, 1);
    };

    const handlePageChange = (event, value) => {
        setPage(value);
        updateURL(search, statusFilter, value);
        // Scroll to top when page changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'contacted': return 'info';
            case 'converted': return 'success';
            case 'rejected': return 'error';
            default: return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending': return <PendingIcon />;
            case 'contacted': return <CheckIcon />;
            case 'converted': return <CheckIcon color="success" />;
            case 'rejected': return <CancelIcon />;
            default: return null;
        }
    };

    const getGenderIcon = (gender) => {
        return gender === 'male' ? '👨' : gender === 'female' ? '👩' : '👤';
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getDiscountPercentage = (maxPrice, price) => {
        return Math.round(((maxPrice - price) / maxPrice) * 100);
    };

    const handleMenuClick = (event, enquiry) => {
        setAnchorEl(event.currentTarget);
        setSelectedEnquiry(enquiry);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedEnquiry(null);
    };

    const handlePrintDetails = () => {
        if (selectedEnquiry) {
            navigate(`/courses/receipt?enquiryid=${selectedEnquiry.enquiryid}`)
        }
        handleMenuClose();
    };

    const handleEdit = () => {
        if (selectedEnquiry) {
            navigate(`/controller/enquiry/edit?id=${selectedEnquiry.enquiryid}`);
        }
        handleMenuClose();
    };

    const handleDelete = async () => {
        if (selectedEnquiry) {
            try {
                // TODO: Implement delete API call
                // await axios.delete(`${apiURL}/franchise/enquiry/delete/${selectedEnquiry.enquiryid}`);

                setEnquiries(enquiries.filter(e => e.enquiryid !== selectedEnquiry.enquiryid));
                toast.success(`Deleted enquiry for ${selectedEnquiry.enq_student_name}`);
                enqueueSnackbar('Enquiry deleted successfully', { variant: 'success' });
            } catch (error) {
                console.error('Error deleting enquiry:', error);
                toast.error('Failed to delete enquiry');
            }
        }
        handleMenuClose();
    };

    const handleConvertClick = () => {
        setEnquiryToConvert(selectedEnquiry);
        setConvertDialogOpen(true);
        handleMenuClose();
    };

    const handleConvertClose = () => {
        if (!convertLoading) {
            setConvertDialogOpen(false);
            setEnquiryToConvert(null);
        }
    };

    const handleConvertConfirm = async () => {
        if (enquiryToConvert) {
            try {
                setConvertLoading(true);

                const formData = new FormData();
                formData.append('enquiryid', enquiryToConvert.enquiryid);

                const response = await axios.post(`${apiURL}/franchise/enquiry/convert`, formData);

                if (response.data.status === 'success' || response.data.status === true) {
                    toast.success(response.data.message || 'Enquiry converted successfully');
                    enqueueSnackbar('Enquiry converted to student', { variant: 'success' });

                    // Refresh the enquiries list
                    fetchEnquiries();
                } else {
                    toast.error(response.data.message || 'Failed to convert enquiry');
                }
            } catch (error) {
                console.error('Error converting enquiry:', error);
                toast.error(error.response?.data?.message || 'Failed to convert enquiry');
            } finally {
                setConvertLoading(false);
                setConvertDialogOpen(false);
                setEnquiryToConvert(null);
            }
        }
    };





    // Calculate statistics from all enquiries
    const totalEnquiries = pagination.total_records;
    const pendingEnquiries = enquiries.filter(e => e.enq_status === 'pending').length;
    const convertedEnquiries = enquiries.filter(e => e.enq_status === 'converted').length;
    const conversionRate = totalEnquiries > 0 ? Math.round((convertedEnquiries / totalEnquiries) * 100) : 0;

    return (
        <Box sx={{ py: 4 }} className="content" >
            <Container maxWidth="xl">
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" fontWeight={600} gutterBottom>
                        Enquiries Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage and track all incoming course enquiries
                    </Typography>
                </Box>

                {/* Stats Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Stack spacing={1.5} alignItems="center" textAlign="center">
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.lighter',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Typography variant="h5" fontWeight={700} color="primary.main">
                                        {totalEnquiries}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        Total Enquiries
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        All enquiries received
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Stack spacing={1.5} alignItems="center" textAlign="center">
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    bgcolor: 'warning.lighter',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Typography variant="h5" fontWeight={700} color="warning.main">
                                        {pendingEnquiries}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        Pending
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Awaiting action
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Stack spacing={1.5} alignItems="center" textAlign="center">
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    bgcolor: 'success.lighter',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Typography variant="h5" fontWeight={700} color="success.main">
                                        {convertedEnquiries}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        Converted
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Successful conversions
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Stack spacing={1.5} alignItems="center" textAlign="center">
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    bgcolor: 'info.lighter',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Typography variant="h5" fontWeight={700} color="info.main">
                                        {conversionRate}%
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        Conversion Rate
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Overall success rate
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>
                </Grid>

                {/* Filters */}
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                        <TextField
                            fullWidth
                            placeholder="Search by name, email, mobile..."
                            value={searchInput}
                            onChange={handleSearch}
                            onKeyDown={handleSearchSubmit}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ flex: 1 }}
                        />

                        <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                            <FormControl size="small" sx={{ minWidth: 150, flex: { xs: 1, sm: 'initial' } }}>
                                <InputLabel>Status Filter</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Status Filter"
                                    onChange={handleStatusFilter}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <FilterIcon fontSize="small" />
                                        </InputAdornment>
                                    }
                                >
                                    {statusOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setSearch('');
                                    setSearchInput('');
                                    setStatusFilter('all');
                                    setPage(1);
                                    setSearchParams(new URLSearchParams());
                                }}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                Clear Filters
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                {/* Enquiries Table */}
                <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                            <CircularProgress size={60} />
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead sx={{ bgcolor: 'action.hover' }}>
                                    <TableRow>
                                        <TableCell><strong>Student</strong></TableCell>
                                        <TableCell><strong>Contact</strong></TableCell>
                                        <TableCell><strong>Referral</strong></TableCell>
                                        <TableCell><strong>Course & Amount</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell><strong>Date</strong></TableCell>
                                        <TableCell align="center"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {enquiries.map((enquiry) => {
                                        const discount = getDiscountPercentage(enquiry.enq_course_amount_max, enquiry.enq_course_amount);

                                        return (
                                            <TableRow
                                                key={enquiry.id}
                                                hover
                                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                            >
                                                {/* Student Info */}
                                                <TableCell>
                                                    <Stack direction="row" alignItems="center" spacing={2}>
                                                        <Avatar sx={{ bgcolor: 'primary.lighter' }}>
                                                            {getGenderIcon(enquiry.enq_student_gender)}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="subtitle2" fontWeight={600}>
                                                                {enquiry.enq_student_name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {enquiry.enq_student_gender === 'male' ? 'Male' : 'Female'}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </TableCell>

                                                {/* Contact Info */}
                                                <TableCell>
                                                    <Stack spacing={0.5}>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <PhoneIcon fontSize="small" color="action" />
                                                            <Typography variant="body2">
                                                                {enquiry.enq_student_mobile}
                                                            </Typography>
                                                        </Stack>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <EmailIcon fontSize="small" color="action" />
                                                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                                {enquiry.enq_student_email}
                                                            </Typography>
                                                        </Stack>
                                                    </Stack>
                                                </TableCell>

                                                {/* Referral Source */}
                                                <TableCell>
                                                    <Chip
                                                        label={referralSourceMap[enquiry.enq_student_from] || enquiry.enq_student_from}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>

                                                {/* Course & Amount */}
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                            {enquiry.course_name}
                                                        </Typography>
                                                        <Stack spacing={0.5}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <MoneyIcon fontSize="small" color="action" />
                                                                <Typography variant="body2">
                                                                    {formatCurrency(enquiry.enq_course_amount)}
                                                                </Typography>
                                                            </Stack>
                                                            {discount > 0 && (
                                                                <Typography variant="caption" color="success.main">
                                                                    {discount}% discount applied
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                </TableCell>

                                                {/* Status */}
                                                <TableCell>
                                                    <Chip
                                                        icon={getStatusIcon(enquiry.enq_status)}
                                                        label={enquiry.enq_status.charAt(0).toUpperCase() + enquiry.enq_status.slice(1)}
                                                        color={getStatusColor(enquiry.enq_status)}
                                                        size="small"
                                                        sx={{ fontWeight: 500 }}
                                                    />
                                                </TableCell>

                                                {/* Date */}
                                                <TableCell>
                                                    <Stack spacing={0.5}>
                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                            <DateIcon fontSize="small" color="action" />
                                                            <Typography variant="body2">
                                                                {formatDate(enquiry.enq_date)}
                                                            </Typography>
                                                        </Stack>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatTime(enquiry.enq_date)}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>

                                                {/* Actions */}
                                                <TableCell align="center">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleMenuClick(e, enquiry)}
                                                    >
                                                        <MoreIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Empty State */}
                    {enquiries.length === 0 && (
                        <Box sx={{ py: 8, textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                No enquiries found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Try adjusting your search or filters
                            </Typography>
                        </Box>
                    )}
                </Card>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            count={pagination.total_pages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            size="large"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                )}



                {/* Actions Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                >
                    <MenuItem onClick={handleEdit}>
                        <EditIcon fontSize="small" sx={{ mr: 1 }} />
                        Edit
                    </MenuItem>
                    <MenuItem onClick={handlePrintDetails}>
                        <PrintIcon fontSize="small" sx={{ mr: 1 }} />
                        Print Details
                    </MenuItem>
                    <MenuItem onClick={handleConvertClick}>
                        <ConvertIcon fontSize="small" sx={{ mr: 1 }} />
                        Convert
                    </MenuItem>
                    {/* <Divider />
                    <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                        Delete
                    </MenuItem> */}
                </Menu>

                {/* Convert Confirmation Dialog */}
                <Dialog
                    open={convertDialogOpen}
                    onClose={handleConvertClose}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        Convert Enquiry to Student?
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to convert this enquiry to a student? This will mark the enquiry as "Converted" and create a student record.
                            {enquiryToConvert && (
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Student Details:
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Name:</strong> {enquiryToConvert.enq_student_name}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Course:</strong> {enquiryToConvert.course_name}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Amount:</strong> {formatCurrency(enquiryToConvert.enq_course_amount)}
                                    </Typography>
                                </Box>
                            )}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleConvertClose} disabled={convertLoading}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConvertConfirm}
                            variant="contained"
                            color="primary"
                            disabled={convertLoading}
                            startIcon={convertLoading ? <CircularProgress size={16} /> : <ConvertIcon />}
                        >
                            {convertLoading ? 'Converting...' : 'Convert'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default EnquiryAdmin;